import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';
import { useConfigurationStore } from 'store/configurationsStore';

import { Aggregation } from 'store/productivityRate.store';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { BarChart } from 'components/common/charts/barChart/barChart';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { Boxes } from 'components/widgets/RunRateChart/components/boxes';
import { aggregationOptions } from 'components/widgets/RunRateChart/productivityRate.constants';
import { useProductivityRateLogic } from 'components/widgets/RunRateChart/productivityRate.logic';
import localClasses from 'components/widgets/RunRateChart/productivityRate.module.scss';
import { useProductivityRateProvider } from 'components/widgets/RunRateChart/productivityRate.provider';

export const ProductivityRate = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().productivityRate;

    const { ui, api } = useProductivityRateProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        widgetViewMode,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propTitle ?? configTitle,
        setSubTitleForExport,
        reportWidgetRef,
        ui,
        apiPayload: api.getProductivityRateData.payload,
        widgetEndpoint: api.getProductivityRateData.endpoint,
    });

    const { logic } = useProductivityRateLogic({
        ui,
        api,
        title: propTitle ?? configTitle,
        subTitle,
        latestVersionId,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <div data-testid={'filters'} key={'filters'} className={classes.filters}>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Aggregation</div>
                                <Select<ISelectOption<Aggregation>>
                                    value={aggregationOptions.find((item) => item.value === ui.state.aggregation)}
                                    options={aggregationOptions}
                                    onChange={(value) => value && ui.setters.onSetAggregation(value.value)}
                                />
                            </div>
                        </div>
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={propTitle ?? configTitle}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getProductivityRateData.payload}
                        widgetEndpoint={api.getProductivityRateData.endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'export'}
                            title={propTitle ?? configTitle}
                            subTitle={subTitle}
                            handleAPICsvExport={logic.handleAPICsvExport}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getProductivityRateData.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={propTitle ?? configTitle}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                <div className={localClasses.boxesAndChartWrapper}>
                    <Boxes data={api.getProductivityRateData.instance.data} ui={ui} />
                    <div className={localClasses.container}>
                        {logic.options && (
                            <BarChart
                                options={logic.options}
                                series={logic.series}
                                isFetching={api.getProductivityRateData.instance.isFetching}
                                noData={
                                    !api.getProductivityRateData.instance.isFetching &&
                                    !api.getProductivityRateData.instance.data
                                }
                                height={logic.chartHeight}
                            />
                        )}
                    </div>
                </div>
            </WidgetWithTitle>
    );
};
